'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MoreHorizontal, FolderTree, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { getStatusBadgeStyle } from '@/lib/badge-utils'

export interface IssueTableItem {
  id: number
  subject: string
  status?: {
    id: number
    name: string
    is_closed?: boolean
  }
  assigned_to?: {
    id: number
    name: string
  }
  project?: {
    id: number
    name: string
  }
  storyPoints?: number | null
}

interface IssuesTableProps {
  issues: IssueTableItem[]
  loading?: boolean
  showProjectName?: boolean
  showStoryPoints?: boolean
  currentProjectId?: number
  onOpenMenu?: (issue: IssueTableItem) => void
  emptyMessage?: string
}

/**
 * Reusable Issues Table Component
 * 
 * Displays issues in a table format with columns:
 * - ID
 * - Subject (with optional project name)
 * - Status
 * - Assignee
 * - Story Points (optional)
 * - Actions
 * 
 * Used in:
 * - Issues list view (/issues page)
 * - Sprint details view (sprint tasks)
 */
export function IssuesTable({
  issues,
  loading = false,
  showProjectName = false,
  showStoryPoints = false,
  currentProjectId,
  onOpenMenu,
  emptyMessage = 'No issues found',
}: IssuesTableProps) {
  const t = useTranslations('issuesPage')

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'id' | 'subject' | 'status' | 'assignee' | 'storyPoints' | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Handle sort column click
  const handleSort = (column: 'id' | 'subject' | 'status' | 'assignee' | 'storyPoints') => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Sort issues based on current sort state
  const sortedIssues = useMemo(() => {
    if (!sortColumn) return issues

    return [...issues].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortColumn) {
        case 'id':
          aValue = a.id
          bValue = b.id
          break
        case 'subject':
          aValue = a.subject.toLowerCase()
          bValue = b.subject.toLowerCase()
          break
        case 'status':
          aValue = a.status?.name.toLowerCase() || ''
          bValue = b.status?.name.toLowerCase() || ''
          break
        case 'assignee':
          aValue = a.assigned_to?.name.toLowerCase() || 'zzz' // Put unassigned at end
          bValue = b.assigned_to?.name.toLowerCase() || 'zzz'
          break
        case 'storyPoints':
          aValue = a.storyPoints ?? -1 // Put null/undefined at end
          bValue = b.storyPoints ?? -1
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [issues, sortColumn, sortDirection])

  // Render sort icon
  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline" />
      : <ArrowDown className="h-3 w-3 ml-1 inline" />
  }

  if (loading) {
    return (
      <div className="overflow-x-auto rounded-md border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-background">
            <tr>
              <th className="px-2 py-2 text-left font-medium text-muted-foreground w-16">
                {t('tableHeaderId')}
              </th>
              <th className="px-2 py-2 text-left font-medium text-muted-foreground max-w-xs lg:max-w-md">
                {t('tableHeaderSubject')}
              </th>
              <th className="px-2 py-2 text-left font-medium text-muted-foreground w-28">
                {t('tableHeaderStatus')}
              </th>
              <th className="px-2 py-2 text-left font-medium text-muted-foreground w-36 hidden md:table-cell">
                {t('tableHeaderAssignee')}
              </th>
              {showStoryPoints && (
                <th className="px-2 py-2 text-center font-medium text-muted-foreground w-16">
                  SP
                </th>
              )}
              <th className="px-2 py-2 text-center font-medium text-muted-foreground w-12 sticky right-0 bg-background">
                {t('tableHeaderActions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
              <tr key={i} className="hover:bg-muted/5">
                <td className="px-2 py-2 align-middle">
                  <Skeleton className="h-4 w-8 rounded-full" />
                </td>
                <td className="px-2 py-2 align-middle max-w-xs lg:max-w-md">
                  <Skeleton className="h-4 w-full rounded-full mb-1" />
                  {showProjectName && (
                    <Skeleton className="h-3 w-32 rounded-full" />
                  )}
                </td>
                <td className="px-2 py-2 align-middle">
                  <Skeleton className="h-5 w-16 rounded-full" />
                </td>
                <td className="px-2 py-2 align-middle hidden md:table-cell">
                  <Skeleton className="h-4 w-24 rounded-full" />
                </td>
                {showStoryPoints && (
                  <td className="px-2 py-2 align-middle text-center">
                    <Skeleton className="h-6 w-6 rounded-full mx-auto" />
                  </td>
                )}
                <td className="px-2 py-2 align-middle sticky right-0 bg-background/95 backdrop-blur-sm">
                  <div className="flex justify-center">
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (issues.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground border rounded-md">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-background">
          <tr>
            <th
              className="px-2 py-2 text-left font-medium text-muted-foreground w-16 cursor-pointer hover:text-foreground transition-colors group"
              onClick={() => handleSort('id')}
            >
              {t('tableHeaderId')}
              <SortIcon column="id" />
            </th>
            <th
              className="px-2 py-2 text-left font-medium text-muted-foreground max-w-xs lg:max-w-md cursor-pointer hover:text-foreground transition-colors group"
              onClick={() => handleSort('subject')}
            >
              {t('tableHeaderSubject')}
              <SortIcon column="subject" />
            </th>
            <th
              className="px-2 py-2 text-left font-medium text-muted-foreground w-28 cursor-pointer hover:text-foreground transition-colors group"
              onClick={() => handleSort('status')}
            >
              {t('tableHeaderStatus')}
              <SortIcon column="status" />
            </th>
            <th
              className="px-2 py-2 text-left font-medium text-muted-foreground w-36 hidden md:table-cell cursor-pointer hover:text-foreground transition-colors group"
              onClick={() => handleSort('assignee')}
            >
              {t('tableHeaderAssignee')}
              <SortIcon column="assignee" />
            </th>
            {showStoryPoints && (
              <th
                className="px-2 py-2 text-center font-medium text-muted-foreground w-16 cursor-pointer hover:text-foreground transition-colors group"
                onClick={() => handleSort('storyPoints')}
              >
                SP
                <SortIcon column="storyPoints" />
              </th>
            )}
            <th className="px-2 py-2 text-center font-medium text-muted-foreground w-12 sticky right-0 bg-background">
              {t('tableHeaderActions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-card divide-y divide-border">
          {sortedIssues.map((issue, idx) => {
            // Show project name if it's from a different project than current
            const isFromDifferentProject = !!(currentProjectId && issue.project?.id !== currentProjectId)
            const shouldShowProject = showProjectName || isFromDifferentProject

            return (
              <tr key={`${issue.id}-${idx}`} className="hover:bg-muted/5">
                <td className="px-2 py-2 align-middle text-xs text-muted-foreground">
                  {issue.id}
                </td>
                <td className="px-2 py-2 align-middle max-w-xs lg:max-w-md">
                  <Link
                    href={`/issues/${issue.id}`}
                    className="font-medium hover:underline block"
                  >
                    <div className="truncate">{issue.subject}</div>
                    {shouldShowProject && issue.project?.name && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <FolderTree className="h-3 w-3" />
                        <span className="truncate">{issue.project.name}</span>
                      </div>
                    )}
                  </Link>
                </td>
                <td className="px-2 py-2 align-middle">
                  {issue.status && (
                    <Badge
                      variant={
                        getStatusBadgeStyle(issue.status.name, issue.status.is_closed ?? false)
                          .variant
                      }
                      className={`text-[10px] font-normal whitespace-nowrap ${
                        getStatusBadgeStyle(issue.status.name, issue.status.is_closed ?? false)
                          .className
                      }`}
                    >
                      {issue.status.name}
                    </Badge>
                  )}
                </td>
                <td className="px-2 py-2 align-middle text-sm whitespace-nowrap hidden md:table-cell">
                  {issue.assigned_to?.name || '—'}
                </td>
                {showStoryPoints && (
                  <td className="px-2 py-2 align-middle text-center">
                    <div
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-primary text-xs font-bold bg-primary/5"
                      style={{ color: 'hsl(var(--primary))' }}
                      title={
                        issue.storyPoints != null
                          ? `Story Points: ${issue.storyPoints}`
                          : 'Story points not set'
                      }
                    >
                      {issue.storyPoints != null ? issue.storyPoints : '-'}
                    </div>
                  </td>
                )}
                <td className="px-2 py-2 align-middle sticky right-0 bg-background/95 backdrop-blur-sm">
                  <div className="flex justify-center">
                    {onOpenMenu ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onOpenMenu(issue)
                        }}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
